/**
 * TaskDetailsAssignees Component
 *
 * Renders the assignees and collaborators management section including:
 * - Team selection (assignment is team-based)
 * - Assignee selection
 * - Collaborators management
 * - Edit mode with save/cancel actions
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button, Card, Row, Col, Tooltip, Select, Divider, Tag, message } from 'antd';
import {
  EditOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  MinusOutlined,
  PlusOutlined,
  UserOutlined,
} from '@ant-design/icons';
import Text from 'antd/lib/typography/Text';
import { startCase } from 'lodash';
import AvatarS3 from '../../../../shared/images/AvatarS3';
import {
  assigneeOptions,
  compareOptionLabels,
  deduplicateUsers,
  matchesOptionLabel,
  getTaskProjectAssigneeGroups,
  getTaskProjects,
} from '../../utils/task.utils';
import { filterEnabledUsers } from '../../../../../core/utils/enabledUserFilter';
import '../../styles/index.scss';

const { Option, OptGroup } = Select;

const TaskDetailsAssignees = ({
  currentTask,
  currentUser,
  level,
  isEditingAssignee,
  setIsEditingAssignee,
  selectedOrg,
  setSelectedOrg,
  selectedAssignees,
  setSelectedAssignees,
  selectedCollaborators,
  setSelectedCollaborators,
  isCollaborator,
  isCurrentUserAssignee,
  isFollowers,
  updateTaskLoading,
  basicOrgsDetails,
  basicOrgsDetailsLoading,
  basicUsersDetails,
  basicUsersDetailsLoading,
  collaboratorsData,
  collaboratorsLoading,
  fetchBasicOrgs,
  fetchBasicUsers,
  fetchCollaborators,
  onUserUpdate,
  onCollaboratorsUpdate,
  onFollowerSelected,
  teams,
  teamsLoading,
  selectedTeamId,
  setSelectedTeamId,
  fetchTeamsByOrg,
}) => {
  return (
    <Card className="task-assignees__card">
      {/* Assigned to Header */}
      <Col className="task-assignees__section-header">
        <Text strong className="task-assignees__section-title">
          Assigned to
        </Text>
        {!isEditingAssignee && level === 'PLATFORM' && (
          <EditOutlined
            className="task-assignees__edit-icon"
            onClick={() => {
              setIsEditingAssignee(true);
              // Fetch teams even for team-only tasks (no assigneeOrg) — fetchTeamsByOrg
              // falls back to the current user's org.
              fetchTeamsByOrg(currentTask?.assigneeOrg?.id);
            }}
          />
        )}
      </Col>

      {/* Team — hidden for project-linked tasks (those are assigned via the project, not a team) */}
      {!currentTask?.project && (
        <Col className="task-assignees__field">
          <Text strong className="task-assignees__label">
            Team
            {isEditingAssignee && (
              <Tooltip title="Changing the team adds the new team's subscribers as collaborators and keeps everyone already on this task — existing collaborators are not removed.">
                <InfoCircleOutlined style={{ marginLeft: 6, color: '#faad14', cursor: 'help' }} />
              </Tooltip>
            )}
          </Text>
          {isEditingAssignee ? (
            <Select
              className="custom-select"
              value={selectedTeamId}
              style={{ width: '100%' }}
              placeholder="Select Team (optional)"
              showSearch
              allowClear
              loading={teamsLoading}
              onFocus={() => fetchTeamsByOrg(selectedOrg)}
              onSearch={(searchText) => fetchTeamsByOrg(selectedOrg, searchText)}
              onChange={(value) => {
                setSelectedTeamId(value || null);
                setSelectedAssignees([]);
              }}
              filterOption={false}
            >
              {(teams || []).map((team) => (
                <Option key={team.id} value={team.id}>
                  {team.name}
                </Option>
              ))}
            </Select>
          ) : (
            <div>
              {currentTask?.teams?.length > 0 ? (
                currentTask.teams.map((team) => (
                  <Tag key={team.id} color="blue">
                    {team.name}
                  </Tag>
                ))
              ) : (
                <Text className="task-assignees__value">-</Text>
              )}
            </div>
          )}
        </Col>
      )}

      {/* Assignee */}
      <Col className="task-assignees__field task-assignees__field--overflow">
        <Text strong className="task-assignees__label">
          Assignee
          {currentTask?.project && (
            <Text className="task-assignees__label-hint">
              (from project team)
            </Text>
          )}
        </Text>
        {isEditingAssignee ? (
          <>
          <Select
            className="custom-select"
            value={selectedAssignees}
            loading={basicUsersDetailsLoading}
            style={{ width: '100%' }}
            placeholder={currentTask?.project ? 'Select from project team' : 'Select Assignee(s)'}
            showSearch
            allowClear
            onSearch={(e) => !currentTask?.project && fetchBasicUsers(e)}
            onChange={(selectedIds) => {
              const followers = currentTask?.followers || [];
              const followerIds = followers.map((user) => user.id);
              if (followerIds.includes(selectedIds)) {
                onFollowerSelected(selectedIds);
              } else {
                setSelectedAssignees(selectedIds);
              }
            }}
            onFocus={() => !currentTask?.project && fetchBasicUsers()}
            filterOption={matchesOptionLabel}
            filterSort={compareOptionLabels}
          >
            {currentTask?.project ? (
              getTaskProjectAssigneeGroups(
                getTaskProjects(currentTask),
                currentTask.assigneeUsers
              ).map((group) => (
                <OptGroup key={group.id} label={group.name}>
                  {group.members.map((user) => (
                    <Option key={`${group.id}-${user.id}`} value={user.id}>
                      {startCase(user.name || user.username)}
                    </Option>
                  ))}
                </OptGroup>
              ))
            ) : (
              (() => {
                // When a team is selected its members are the source of truth (works even for
                // team-only tasks where basicUsersDetails was never fetched); otherwise fall back
                // to the org's user list — so a user can be picked without choosing a team.
                // Team members aren't server-filtered, so drop disabled ones here.
                const selectedTeam = (teams || []).find((t) => t.id === selectedTeamId);
                const baseUsers = selectedTeamId
                  ? selectedTeam && selectedTeam.members
                    ? filterEnabledUsers(selectedTeam.members)
                    : []
                  : basicUsersDetails;
                return deduplicateUsers(
                  baseUsers.concat(
                    currentTask?.assigneeUsers?.filter(
                      (u) => !baseUsers.some((bu) => bu.id === u.id)
                    ) || []
                  )
                )
                  .map((user) => (
                    <Option key={user.id} value={user.id}>
                      {startCase(user.name || user.username)}
                    </Option>
                  ));
              })()
            )}
          </Select>
          {!currentTask?.project && (
            <Text className="task-assignees__helper-text">
              Optional — pick any user, or select a team to narrow to its members
            </Text>
          )}
          </>
        ) : (
          <Text className="task-assignees__value">
            {currentTask &&
            currentTask.assigneeUsers &&
            currentTask.assigneeUsers.length > 0
              ? currentTask.assigneeUsers.map((user, index) => (
                  <Row key={index} className="task-assignees__assignee-row">
                    {startCase(user.name || user.username)}
                  </Row>
                ))
              : startCase(
                  currentTask && currentTask.assigneeUsers && currentTask.assigneeUsers.name
                ) || '-'}
          </Text>
        )}
      </Col>

      <Col>
        <Divider />
      </Col>

      {/* Collaborators Header */}
      <Col className="task-assignees__collaborators-header">
        <Col className="task-assignees__collaborators-title-row">
          <UserOutlined className="task-assignees__collaborators-icon" />
          <Text strong className="task-assignees__section-title">
            Collaborators
          </Text>
        </Col>
        <Col>
          {level === 'PLATFORM' && !isEditingAssignee && (
            <Button
              className="task-assignees__action-btn"
              disabled={updateTaskLoading}
              onClick={onCollaboratorsUpdate}
            >
              {isCollaborator ? (
                <>
                  <MinusOutlined className="task-assignees__action-icon task-assignees__action-icon--leave" />
                  <Text className="task-assignees__action-text task-assignees__action-text--leave">Leave</Text>
                </>
              ) : (
                <>
                  <PlusOutlined className="task-assignees__action-icon task-assignees__action-icon--join" />
                  <Text className="task-assignees__action-text task-assignees__action-text--join">Join</Text>
                </>
              )}
            </Button>
          )}
        </Col>
      </Col>

      {/* Collaborators List or Edit */}
      {!isEditingAssignee ? (
        <Col>
          {currentTask &&
          currentTask.collaborators &&
          currentTask.collaborators.length > 0 ? (
            <div className="task-assignees__collaborators-list">
              {currentTask.collaborators.map((collaborator, idx) => (
                <Tooltip
                  key={collaborator.id || idx}
                  title={startCase(collaborator.name || collaborator.username)}
                >
                  <div className="task-assignees__collaborator-item">
                    <AvatarS3
                      key={collaborator.id || idx}
                      imgKey={collaborator.avatar && collaborator.avatar.key}
                      username={collaborator.name || collaborator.username}
                      avatarProps={{ size: 40 }}
                    />
                  </div>
                </Tooltip>
              ))}
            </div>
          ) : (
            <Card className="task-assignees__empty-card">
              <Text>
                Collaborators receive the same alerts as the assignee and can actively help
                complete the task.
              </Text>
            </Card>
          )}
        </Col>
      ) : (
        <>
          <Col>
            <Select
              className="custom-select"
              mode="multiple"
              value={selectedCollaborators}
              loading={collaboratorsLoading}
              style={{ width: '100%' }}
              placeholder="Select collaborator(s)"
              onSearch={(e) => fetchCollaborators(e)}
              onChange={(selectedIds) => {
                setSelectedCollaborators(selectedIds);
              }}
              onFocus={() => fetchCollaborators()}
              filterOption={false}
              filterSort={(optionA, optionB) => {
                return optionA.children
                  .toLowerCase()
                  .localeCompare(optionB.children.toLowerCase());
              }}
            >
              {assigneeOptions(collaboratorsData, currentTask?.collaborators).map((user) => (
                <Option key={user.id} value={user.id}>
                  {startCase(user.name || user.username)}
                </Option>
              ))}
            </Select>
          </Col>
          <Col>
            <Divider />
          </Col>
          <div className="task-assignees__edit-actions">
            <Button
              type="primary"
              onClick={onUserUpdate}
              disabled={updateTaskLoading}
              className="task-assignees__save-btn"
            >
              {updateTaskLoading && <LoadingOutlined />} Save
            </Button>
            <Button
              onClick={() => {
                setIsEditingAssignee(false);
                setSelectedOrg(
                  currentTask && currentTask.assigneeOrg && currentTask.assigneeOrg.id
                    ? currentTask.assigneeOrg.id
                    : null
                );
                setSelectedAssignees(
                  (currentTask &&
                    currentTask.assigneeUsers &&
                    currentTask.assigneeUsers.map((user) => user.id)) ||
                    []
                );
                setSelectedCollaborators(
                  currentTask &&
                    currentTask.collaborators &&
                    currentTask.collaborators.length > 0
                    ? currentTask.collaborators.map((user) => user.id)
                    : []
                );
                setSelectedTeamId(currentTask?.teams?.[0]?.id || null);
              }}
              className="task-assignees__cancel-btn"
              disabled={updateTaskLoading}
            >
              Cancel
            </Button>
          </div>
        </>
      )}
    </Card>
  );
};

TaskDetailsAssignees.propTypes = {
  currentTask: PropTypes.shape({
    id: PropTypes.string,
    assigneeOrg: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
    assigneeUsers: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      username: PropTypes.string,
    })),
    collaborators: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      username: PropTypes.string,
      avatar: PropTypes.shape({
        key: PropTypes.string,
      }),
    })),
    followers: PropTypes.array,
    project: PropTypes.shape({
      id: PropTypes.string,
      owner: PropTypes.object,
      team: PropTypes.array,
    }),
  }),
  currentUser: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  level: PropTypes.string,
  isEditingAssignee: PropTypes.bool.isRequired,
  setIsEditingAssignee: PropTypes.func.isRequired,
  selectedOrg: PropTypes.string,
  setSelectedOrg: PropTypes.func.isRequired,
  selectedAssignees: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  setSelectedAssignees: PropTypes.func.isRequired,
  selectedCollaborators: PropTypes.array,
  setSelectedCollaborators: PropTypes.func.isRequired,
  isCollaborator: PropTypes.bool,
  isCurrentUserAssignee: PropTypes.bool,
  isFollowers: PropTypes.bool,
  updateTaskLoading: PropTypes.bool,
  basicOrgsDetails: PropTypes.array,
  basicOrgsDetailsLoading: PropTypes.bool,
  basicUsersDetails: PropTypes.array,
  basicUsersDetailsLoading: PropTypes.bool,
  collaboratorsData: PropTypes.array,
  collaboratorsLoading: PropTypes.bool,
  fetchBasicOrgs: PropTypes.func.isRequired,
  fetchBasicUsers: PropTypes.func.isRequired,
  fetchCollaborators: PropTypes.func.isRequired,
  onUserUpdate: PropTypes.func.isRequired,
  onCollaboratorsUpdate: PropTypes.func.isRequired,
  onFollowerSelected: PropTypes.func.isRequired,
  teams: PropTypes.array,
  teamsLoading: PropTypes.bool,
  selectedTeamId: PropTypes.string,
  setSelectedTeamId: PropTypes.func,
  fetchTeamsByOrg: PropTypes.func,
};

TaskDetailsAssignees.defaultProps = {
  currentTask: null,
  level: null,
  selectedOrg: null,
  selectedAssignees: [],
  selectedCollaborators: [],
  isCollaborator: false,
  isCurrentUserAssignee: false,
  isFollowers: false,
  updateTaskLoading: false,
  basicOrgsDetails: [],
  basicOrgsDetailsLoading: false,
  basicUsersDetails: [],
  basicUsersDetailsLoading: false,
  collaboratorsData: [],
  collaboratorsLoading: false,
  teams: [],
  teamsLoading: false,
  selectedTeamId: null,
  setSelectedTeamId: () => {},
  fetchTeamsByOrg: () => {},
};

export default TaskDetailsAssignees;
